/**
 * The family's decisions, taken once, away from any composition.
 *
 * Five surfaces ask the same four questions — is there a clip a reader can
 * play, which clip is it, how does this vendor's vocabulary read in English,
 * and what does the prescription say — and five answers is five places for one
 * of them to drift. The video state in particular is derived against the CLOCK
 * on every call rather than decided once at capture, because the whole point of
 * a 48-hour signature is that the same data means something different tomorrow.
 */
/**
 * The clock helper comes from the kit's format LEAF rather than its barrel: the
 * barrel re-exports a surface that imports a stylesheet, and this module's own
 * test runs under `node --import tsx --test`, which has no CSS loader. The type
 * import is erased, so it takes the barrel like everything else.
 */
import { formatClockFromSeconds } from "@/components/ui/Cresultcard/cresultcard.format";
import type { CresultcardAsset } from "@/components/ui/Cresultcard";
import {
  C_WORKOUT_SHOOT_LABEL,
  CWORKOUTCARD_REST_LABEL,
} from "./cworkoutcard.constants";
import {
  CworkoutcardMoveReading,
  CworkoutcardOrientation,
  CworkoutcardPhase,
  CworkoutcardShoot,
  CworkoutcardVideoState,
  type CworkoutcardClip,
  type CworkoutcardData,
  type CworkoutcardMedia,
  type CworkoutcardPlanData,
  type CworkoutcardPlanSlot,
  type CworkoutcardPrescription,
} from "./cworkoutcard.types";

const VENDOR_WORD_SEPARATOR = /[_\s-]+/u;
const WORD_JOINER = " ";
const FACT_SEPARATOR = " · ";
const SETS_SEPARATOR = " × ";
const REST_SUFFIX = "s";

/** The row key: the vendor's own handle, which every question kind carries. */
export function cworkoutcardKey(data: CworkoutcardData): string {
  return data.key;
}

/**
 * A vendor slug as a sentence: `full_body` → `Full body`, `erector_spinae` →
 * `Erector spinae`, `YOGA` → `Yoga`. The vocabulary arrives in three casings on
 * one record — a screamed `category`, a snake-cased `muscleGroup`, a lowercase
 * `equipment` — and printing all three verbatim makes one record look like
 * three sources.
 */
export function cworkoutcardVendorWords(value: string): string {
  const words = value
    .split(VENDOR_WORD_SEPARATOR)
    .filter((word) => word.length > 0)
    .map((word) => word.toLowerCase());
  const [first, ...rest] = words;
  if (first == null) {
    return value;
  }
  return [first.slice(0, 1).toUpperCase() + first.slice(1), ...rest].join(
    WORD_JOINER,
  );
}

/** The vendor's shoot tag, or `null` where it named a shoot outside its own two flags. */
export function cworkoutcardShoot(tag: string): CworkoutcardShoot | null {
  return (
    Object.values(CworkoutcardShoot).find((shoot) => shoot === tag) ?? null
  );
}

/** The vendor's framing word, or `null` where it named one this family does not know. */
export function cworkoutcardOrientation(
  value: string,
): CworkoutcardOrientation | null {
  return (
    Object.values(CworkoutcardOrientation).find(
      (orientation) => orientation === value,
    ) ?? null
  );
}

/** The shoot spelled for a reader, or `null` where the tag was unrecognised. */
export function cworkoutcardShootLabel(
  shoot: CworkoutcardShoot | null,
): string | null {
  return shoot == null ? null : C_WORKOUT_SHOOT_LABEL[shoot];
}

/**
 * The clip a surface leads with: the one the vendor flagged primary, and
 * otherwise the first it sent. Never a "best" derived by this family — the
 * order is the vendor's and re-ranking it would invent a judgement the capture
 * does not carry.
 */
export function cworkoutcardPrimaryClip(
  media: CworkoutcardMedia | null,
): CworkoutcardClip | null {
  if (media == null) {
    return null;
  }
  return media.clips.find((clip) => clip.primary) ?? media.clips[0] ?? null;
}

/** The poster triple of the leading clip, where one was published. */
export function cworkoutcardPoster(
  media: CworkoutcardMedia | null,
): CresultcardAsset | null {
  return cworkoutcardPrimaryClip(media)?.poster ?? null;
}

export interface CworkoutcardVideoStateInput {
  readonly data: CworkoutcardData;
  /** The instant the state is being asked about. Never defaulted inside. */
  readonly now: Date;
  /** True once the mounted player has refused the source. */
  readonly playbackFailed: boolean;
}

/**
 * What a reader can do about this exercise's demonstration at `now`.
 *
 * The order of the guards is the order of the truths: a player that already
 * refused the source outranks a signature that has not expired yet, an expired
 * signature outranks the clips it signs, clips outrank the catalog's boolean,
 * and the boolean is the last word only when nothing above it applies.
 */
export function cworkoutcardVideoState({
  data,
  now,
  playbackFailed,
}: CworkoutcardVideoStateInput): CworkoutcardVideoState {
  const clip = cworkoutcardPrimaryClip(data.media);
  if (data.media == null || clip == null) {
    return data.hasVideo
      ? CworkoutcardVideoState.Unresolved
      : CworkoutcardVideoState.Absent;
  }
  // A null `expiresAt` is a durable address rather than an unknown deadline:
  // nothing was signed, so nothing can have run out.
  const expiresAt =
    data.media.expiresAt === null ? Number.NaN : Date.parse(data.media.expiresAt);
  if (!Number.isNaN(expiresAt) && expiresAt <= now.getTime()) {
    return CworkoutcardVideoState.Expired;
  }
  if (playbackFailed) {
    return CworkoutcardVideoState.Failed;
  }
  return CworkoutcardVideoState.Playable;
}

/** Only one state hands a working source to a player. */
export function cworkoutcardIsPlayable(state: CworkoutcardVideoState): boolean {
  return state === CworkoutcardVideoState.Playable;
}

/**
 * The clip's own length as a clock, where anything measured one.
 *
 * The catalog and the media call both publish `videoDurationSecs: null` on
 * every captured record, so this returns `null` far more often than it returns
 * a clock — and a surface that needs a duration renders nothing rather than a
 * zero.
 */
export function cworkoutcardDuration(
  media: CworkoutcardMedia | null,
): string | null {
  const seconds = media?.durationSecs ?? null;
  if (seconds == null || seconds <= 0) {
    return null;
  }
  return formatClockFromSeconds(seconds);
}

/** The prescription as one line: `3 × 8-12 · 75s rest`. */
export function cworkoutcardPrescriptionLine(
  prescription: CworkoutcardPrescription,
): string {
  const load = `${String(prescription.sets)}${SETS_SEPARATOR}${prescription.repetitions}`;
  const rest = `${String(prescription.restSeconds)}${REST_SUFFIX} ${CWORKOUTCARD_REST_LABEL}`;
  return `${load}${FACT_SEPARATOR}${rest}`;
}

/** Primary muscle first, then whatever else the record says the movement works. */
export function cworkoutcardMuscles(data: CworkoutcardData): readonly string[] {
  return [data.muscleGroup, ...data.secondaryMuscles]
    .filter((muscle) => muscle.length > 0)
    .map(cworkoutcardVendorWords);
}

export interface CworkoutcardMoveTextInput {
  readonly data: CworkoutcardData;
  readonly reading: CworkoutcardMoveReading;
}

/**
 * The movement in the selected register, as LINES.
 *
 * The narration is one paragraph, so it is one line; the steps are however many
 * the vendor sent. Returning both shapes as a line list is what lets one
 * renderer draw either without asking which it got — and an empty result is the
 * honest answer for a plan slot, whose projection of the same movement carries
 * the paragraph and no steps at all.
 */
export function cworkoutcardMoveLines({
  data,
  reading,
}: CworkoutcardMoveTextInput): readonly string[] {
  if (reading === CworkoutcardMoveReading.Narration) {
    return data.description.length > 0 ? [data.description] : [];
  }
  return data.instructions.length > 0
    ? data.instructions
    : data.description.length > 0
      ? [data.description]
      : [];
}

/** True where the selected reading fell back to the other one's field. */
export function cworkoutcardMoveIsSubstituted({
  data,
  reading,
}: CworkoutcardMoveTextInput): boolean {
  return (
    reading === CworkoutcardMoveReading.Steps && data.instructions.length === 0
  );
}

export interface CworkoutcardPhaseGroup {
  readonly phase: CworkoutcardPhase;
  readonly slots: readonly CworkoutcardPlanSlot[];
}

/**
 * A plan's slots grouped into the phases it runs them in, with empty phases
 * dropped. The captured plan carries a warm-up and a main block and an EMPTY
 * cool-down, and drawing a `Cool-down` heading over nothing is a section that
 * says the vendor failed when the request simply did not ask for one.
 */
export function cworkoutcardPlanPhases(
  plan: CworkoutcardPlanData,
): readonly CworkoutcardPhaseGroup[] {
  return [
    CworkoutcardPhase.Warmup,
    CworkoutcardPhase.Main,
    CworkoutcardPhase.Cooldown,
  ]
    .map((phase) => ({
      phase,
      slots: plan.slots.filter((slot) => slot.phase === phase),
    }))
    .filter((group) => group.slots.length > 0);
}

/** How many movements the plan actually prescribes, across every phase. */
export function cworkoutcardPlanSlotCount(plan: CworkoutcardPlanData): number {
  return plan.slots.length;
}
